/**
 * Breadcrumb Toolbar Carousel


 * - Smooth one-chip-at-a-time navigation
 * - Proper arrow visibility (hide at boundaries)
 */
(function() {
	'use strict';

	class BreadcrumbCarousel {
		constructor(root) {
			this.root = root;
			this.carousel = root.querySelector('.breadcrumbs-carousel');
			this.btnLeft = root.querySelector('.breadcrumbs-arrow--left');
			this.btnRight = root.querySelector('.breadcrumbs-arrow--right');
			
			if (!this.carousel) return;
			
			this.state = {
				isScrolling: false
			};
			
			this.GAP = 16; // Must match CSS gap
			this.MAX_VISIBLE_CHIPS = 6;
			
			this.init();
		}

		init() {
			this.setupEventListeners();
			// Initial setup with slight delay to ensure DOM is ready
			setTimeout(() => this.update(), 100);
		}

		setupEventListeners() {
			// Arrow click handlers
			if (this.btnLeft) {
				this.btnLeft.addEventListener('click', () => this.scrollLeft());
			}
			if (this.btnRight) {
				this.btnRight.addEventListener('click', () => this.scrollRight());
			}

			// Debounced update on scroll
			this.carousel.addEventListener('scroll', this.debounce(() => {
				if (!this.state.isScrolling) {
					this.updateArrowVisibility();
				}
			}, 100), { passive: true });

			// Update on resize
			window.addEventListener('resize', this.debounce(() => this.update(), 150));

			// Update when chips change (e.g., AJAX updates)
			const observer = new MutationObserver(() => this.update());
			observer.observe(this.carousel, { childList: true, subtree: true });
		}

		getChips() {
			return Array.from(this.carousel.querySelectorAll('.breadcrumb-item'));
		}

		calculateMaxWidth() {
			const chips = this.getChips();
			if (chips.length === 0) {
				this.root.style.removeProperty('--breadcrumbs-visible-max');
				return;
			}

			// Calculate width for up to 6 chips
			const visibleCount = Math.min(this.MAX_VISIBLE_CHIPS, chips.length);
			let totalWidth = 0;

			for (let i = 0; i < visibleCount; i++) {
				const chipWidth = chips[i].getBoundingClientRect().width;
				totalWidth += chipWidth;
			}

			// Add gaps between chips (n-1 gaps for n chips)
			totalWidth += (visibleCount - 1) * this.GAP;

			// Add small padding for safety
			totalWidth += 4;

			// Set the CSS variable
			this.root.style.setProperty('--breadcrumbs-visible-max', `${totalWidth}px`);
		}	
		updateArrowVisibility() {
			const scrollLeft = this.carousel.scrollLeft;
			const scrollWidth = this.carousel.scrollWidth;
			const clientWidth = this.carousel.clientWidth;
			const maxScroll = scrollWidth - clientWidth;

			// No overflow - hide both arrows
			if (maxScroll <= 1) {
				this.root.classList.add('breadcrumbs--no-overflow');
				if (this.btnLeft) {
					this.btnLeft.hidden = true;
					this.btnLeft.style.pointerEvents = 'none';
				}
				if (this.btnRight) {
					this.btnRight.hidden = true;
					this.btnRight.style.pointerEvents = 'none';
				}
				return;
			}

			// Has overflow - show/hide based on position
			this.root.classList.remove('breadcrumbs--no-overflow');
			
			// Hide left arrow if at the start (with 2px tolerance)
			if (this.btnLeft) {
				const atStart = scrollLeft <= 2;
				this.btnLeft.hidden = atStart;
				this.btnLeft.style.pointerEvents = atStart ? 'none' : 'auto';
			}
			
			// Hide right arrow if at the end (with 2px tolerance)
			if (this.btnRight) {
				const atEnd = scrollLeft >= maxScroll - 2;
				this.btnRight.hidden = atEnd;
				this.btnRight.style.pointerEvents = atEnd ? 'none' : 'auto';
			}
		}

		scrollLeft() {
			if (this.state.isScrolling) return;
			
			const chips = this.getChips();
			if (chips.length === 0) return;

			const currentScroll = this.carousel.scrollLeft;
			
			// If we're at the very start, do nothing
			if (currentScroll <= 2) return;
			
			// Find the first fully visible chip's index
			let currentVisibleIndex = 0;
			for (let i = 0; i < chips.length; i++) {
				const chipLeft = chips[i].offsetLeft;
				if (chipLeft >= currentScroll - 1) {
					currentVisibleIndex = i;
					break;
				}
			}
			
			// Go to previous chip (or stay at first)
			const targetIndex = Math.max(0, currentVisibleIndex - 1);
			
			// If going to first chip, scroll all the way to 0 to ensure it's fully visible
			if (targetIndex === 0) {
				this.scrollToPosition(0);
			} else {
				this.scrollToChip(chips[targetIndex]);
			}
		}

		scrollRight() {
			if (this.state.isScrolling) return;
			
			const chips = this.getChips();
			if (chips.length === 0) return;

			const currentScroll = this.carousel.scrollLeft;
			const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth;
			
			// If we're at the very end, do nothing
			if (currentScroll >= maxScroll - 1) return;
			
			// Find the first fully visible chip's index
			let currentVisibleIndex = 0;
			for (let i = 0; i < chips.length; i++) {
				const chipLeft = chips[i].offsetLeft;
				if (chipLeft >= currentScroll - 1) {
					currentVisibleIndex = i;
					break;
				}
			}
			
			// Go to next chip (or stay at last)
			const targetIndex = Math.min(chips.length - 1, currentVisibleIndex + 1);
			this.scrollToChip(chips[targetIndex]);
		}

		scrollToChip(chip) {
			if (!chip) return;
			
			const targetScroll = chip.offsetLeft;
			this.scrollToPosition(targetScroll);
		}

		scrollToPosition(targetScroll) {
			this.state.isScrolling = true;
			
			this.carousel.scrollTo({
				left: targetScroll,
				behavior: 'smooth'
			});

			// Reset scrolling flag after animation
			setTimeout(() => {
				this.state.isScrolling = false;
				this.updateArrowVisibility();
			}, 400); // Faster response time
		}

		update() {
			this.calculateMaxWidth();
			this.updateArrowVisibility();
		}

		debounce(func, wait) {
			let timeout;
			return function executedFunction(...args) {
				const later = () => {
					clearTimeout(timeout);
					func(...args);
				};
				clearTimeout(timeout);
				timeout = setTimeout(later, wait);
			};
		}
	}

	// Initialize all breadcrumb carousels on the page
	function initCarousels() {
		const toolbars = document.querySelectorAll('.breadcrumbs-section--toolbar');
		toolbars.forEach(toolbar => {
			new BreadcrumbCarousel(toolbar);
		});
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initCarousels);
	} else {
		initCarousels();
	}
	})();
